import { Link } from "react-router-dom";
import { Phone, Mail, MapPin, Instagram, Facebook } from "lucide-react";
import CredentialsBar from "@/components/CredentialsBar";

const quickLinks = [
  { label: "Home", to: "/" },
  { label: "Products", to: "/products" },
  { label: "Compare Fabrics", to: "/compare" },
  { label: "Order Now", to: "/order" },
  { label: "Shade Catalogue", to: "/catalogue" },
  { label: "Sample Kit · ₹299", to: "/sample-kit" },
  { label: "Heritage", to: "/heritage" },
  { label: "Delivery", to: "/delivery" },
  { label: "Gallery", to: "/gallery" },
  { label: "Contact", to: "/contact" },
  { label: "Exchange Policy", to: "/exchange-policy" },
];

export default function Footer() {
  return (
    <>
      <CredentialsBar />
    <footer
      style={{ backgroundColor: "var(--mp-solid)" }}
      className="text-white"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-16 lg:py-20">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-10 lg:gap-12">
          {/* Brand */}
          <div className="lg:col-span-1">
            <img
              src="/brand/lockup-stacked-light.svg"
              alt="Mutha Paras & Co. — Lining Fabric Since 1974"
              className="h-24 w-auto mb-6"
              loading="lazy"
              data-testid="footer-logo"
            />
            <p
              className="text-sm leading-relaxed opacity-80"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              India&apos;s leading lining fabric manufacturer and wholesaler.
              Serving the textile industry for over 50 years with
              premium quality fabrics in 2,400+ colours and 15,00,000+
              meters of ready stock — with up to 8,000 m available per
              running shade.
            </p>
            {/* Social Media Links */}
            <div className="flex items-center gap-3 mt-5">
              <a
                href="https://instagram.com/theliningstoreindia"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 hover:opacity-100 opacity-70"
                style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}
                data-testid="footer-instagram-link"
              >
                <Instagram className="w-4 h-4" />
              </a>
              <a
                href="https://facebook.com/muthaparas"
                target="_blank"
                rel="noopener noreferrer"
                className="w-9 h-9 rounded-full flex items-center justify-center transition-all duration-300 hover:opacity-100 opacity-70"
                style={{ backgroundColor: 'rgba(255,255,255,0.12)' }}
                data-testid="footer-facebook-link"
              >
                <Facebook className="w-4 h-4" />
              </a>
            </div>
          </div>

          {/* Quick Links */}
          <div>
            <h4
              className="text-sm font-semibold uppercase tracking-wider mb-5"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Quick Links
            </h4>
            <nav className="space-y-3">
              {quickLinks.map((link) => (
                <Link
                  key={link.to}
                  to={link.to}
                  className="block text-sm opacity-80 hover:opacity-100 transition-opacity duration-200"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                  data-testid={`footer-link-${link.label.toLowerCase().replace(/\s/g, "-")}`}
                >
                  {link.label}
                </Link>
              ))}
            </nav>
          </div>

          {/* Products */}
          <div>
            <h4
              className="text-sm font-semibold uppercase tracking-wider mb-5"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Our Products
            </h4>
            <div className="space-y-3">
              {[
                { name: "600 Lining", id: "600-lining" },
                { name: "600 Gold", id: "600-gold", isNew: true },
                { name: "060 Lining", id: "060-lining" },
                { name: "006 Lining", id: "006-lining" },
                { name: "6000 Shrinkless", id: "6000-shrinkless" },
                { name: "Super Queen", id: "super-queen" },
                { name: "303 Pure Cotton", id: "303-pure-cotton" },
                { name: "3000 Pure Cotton", id: "3000-pure-cotton" },
                { name: "American Crepe", id: "american-crepe" },
                { name: "600 Gold Cut Piece", id: "600-gold-cut-piece", isNew: true },
              ].map((p) => (
                <Link
                  key={p.id}
                  to={`/product/${p.id}`}
                  className="flex items-center gap-2 text-sm opacity-80 hover:opacity-100 transition-opacity duration-200"
                  style={{ fontFamily: "'Manrope', sans-serif" }}
                  aria-label={p.isNew ? `${p.name} — new quality` : p.name}
                  data-testid={`footer-product-${p.id}`}
                >
                  {p.name}
                  {p.isNew && (
                    <span
                      className="text-[9px] font-semibold uppercase tracking-wider px-1.5 py-0.5 rounded-sm"
                      style={{ backgroundColor: "var(--mp-copper-solid)", color: "#FFFFFF" }}
                    >
                      New
                    </span>
                  )}
                </Link>
              ))}
            </div>
          </div>

          {/* Contact */}
          <div>
            <h4
              className="text-sm font-semibold uppercase tracking-wider mb-5"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              Contact Us
            </h4>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <Phone className="w-4 h-4 mt-0.5 flex-shrink-0 opacity-80" />
                <div>
                  <a href="tel:+917624853840" className="text-sm block opacity-80 hover:opacity-100" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    +91 7624853840
                  </a>
                  <a href="tel:08022200274" className="text-sm block opacity-80 hover:opacity-100" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    080 22200274
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <Mail className="w-4 h-4 mt-0.5 flex-shrink-0 opacity-80" />
                <div>
                  <a href="mailto:muthaparas1975@gmail.com" className="text-sm block opacity-80 hover:opacity-100" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    muthaparas1975@gmail.com
                  </a>
                  <a href="mailto:muthaparas1975@hotmail.com" className="text-sm block opacity-80 hover:opacity-100" style={{ fontFamily: "'Manrope', sans-serif" }}>
                    muthaparas1975@hotmail.com
                  </a>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <MapPin className="w-4 h-4 mt-0.5 flex-shrink-0 opacity-80" />
                <p className="text-sm opacity-80 leading-relaxed" style={{ fontFamily: "'Manrope', sans-serif" }}>
                  Kochaar Muthas Enclave No. 28,
                  <br />
                  Venkusahuji Lane, Chickpet Cross,
                  <br />
                  Bangalore - 560053
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Bottom Bar */}
        <div
          className="mt-14 pt-8 border-t flex flex-col md:flex-row items-center justify-between gap-4"
          style={{ borderColor: 'rgba(255,255,255,0.15)' }}
        >
          <p className="text-xs opacity-60" style={{ fontFamily: "'Manrope', sans-serif" }}>
            &copy; {new Date().getFullYear()} Mutha Paras & Co. All rights reserved.
          </p>
          <p className="text-xs opacity-60" style={{ fontFamily: "'Manrope', sans-serif" }}>
            50+ Years of Trusted Quality in Lining Fabrics
          </p>
        </div>
      </div>
    </footer>
    </>
  );
}
